import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCreateManualTime, useProjectTasks, type ProjectRef } from "@/api/queries";
import { Field } from "@/components/common/Field";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { Input, NativeSelect } from "@/components/ui/input";
import { errorText, fieldError } from "@/lib/errors";
import { dateInZone, formatDuration, shiftDate, zonedToISO } from "@/lib/format";

/** How far back manual time may go, as on the server. */
const maxAgeDays = 30;

type ManualTimeDialogProps = {
  /** Time zone of the person the time is for: the form works in their local time. */
  timeZone: string;
  projects: ProjectRef[];
  defaultDate: string;
  approvalRequired: boolean;
  /** Set when an administrator adds time for someone else. */
  forUser?: { id: string; full_name: string };
  onClose: () => void;
};

/** Adds a manual time interval: date, from–to, project, task and reason. */
export function ManualTimeDialog({
  timeZone,
  projects,
  defaultDate,
  approvalRequired,
  forUser,
  onClose,
}: ManualTimeDialogProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const today = dateInZone(new Date(), timeZone);
  const [date, setDate] = useState(defaultDate > today ? today : defaultDate);
  const [from, setFrom] = useState("09:00");
  const [to, setTo] = useState("10:00");
  const [projectId, setProjectId] = useState(projects[0]?.id ?? "");
  const [taskId, setTaskId] = useState("");
  const [reason, setReason] = useState("");
  const tasks = useProjectTasks(projectId);
  const create = useCreateManualTime();

  const overnight = Boolean(from && to) && to <= from;
  const startISO = date && from ? zonedToISO(date, from, timeZone) : "";
  const endISO = date && to ? zonedToISO(overnight ? shiftDate(date, 1) : date, to, timeZone) : "";
  const seconds = startISO && endISO ? (Date.parse(endISO) - Date.parse(startISO)) / 1000 : 0;
  const valid = seconds >= 60 && Boolean(projectId) && reason.trim().length > 0;
  const openTasks = (tasks.data?.items ?? []).filter((task) => task.status !== "done");
  const timeError = fieldError(t, create.error, "ended_at") ?? fieldError(t, create.error, "started_at");
  const knownFields = ["ended_at", "started_at", "project_id", "task_id", "reason"];
  const otherError =
    create.error && !knownFields.some((f) => fieldError(t, create.error, f))
      ? errorText(t, create.error)
      : null;

  const submit = async () => {
    await create.mutateAsync({
      user_id: forUser?.id,
      project_id: projectId,
      task_id: taskId || undefined,
      started_at: startISO,
      ended_at: endISO,
      reason: reason.trim(),
    });
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={t("manual.add.title")}
        description={
          forUser ? t("manual.add.forUser", { name: forUser.full_name }) : t("manual.add.description")
        }
      >
        <form
          className="flex flex-col gap-4 p-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) void submit().catch(() => undefined);
          }}
        >
          {projects.length === 0 ? (
            <Alert variant="warning">{t("manual.add.noProjects")}</Alert>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1.4fr_1fr_1fr]">
                <Field label={t("manual.add.date")}>
                  <Input
                    type="date"
                    value={date}
                    min={shiftDate(today, -maxAgeDays)}
                    max={today}
                    required
                    onChange={(e) => setDate(e.target.value)}
                  />
                </Field>
                <Field label={t("manual.add.from")}>
                  <Input type="time" value={from} required onChange={(e) => setFrom(e.target.value)} />
                </Field>
                <Field label={t("manual.add.to")}>
                  <Input type="time" value={to} required onChange={(e) => setTo(e.target.value)} />
                </Field>
              </div>
              <p
                className={timeError ? "-mt-2 text-xs text-danger" : "-mt-2 text-xs text-muted"}
                aria-live="polite"
              >
                {timeError ??
                  (seconds > 0
                    ? t(overnight ? "manual.add.durationOvernight" : "manual.add.duration", {
                        time: formatDuration(seconds, lang),
                      })
                    : t("manual.add.timezone", { tz: timeZone }))}
              </p>
              <Field label={t("manual.add.project")} error={fieldError(t, create.error, "project_id")}>
                <NativeSelect
                  value={projectId}
                  onChange={(e) => {
                    setProjectId(e.target.value);
                    setTaskId("");
                  }}
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label={t("manual.add.task")} error={fieldError(t, create.error, "task_id")}>
                <NativeSelect value={taskId} onChange={(e) => setTaskId(e.target.value)}>
                  <option value="">{t("manual.noTask")}</option>
                  {openTasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.title}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field
                label={t("manual.add.reason")}
                hint={t("manual.add.reasonHint")}
                error={fieldError(t, create.error, "reason")}
              >
                <textarea
                  value={reason}
                  maxLength={500}
                  rows={3}
                  required
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full rounded-lg border bg-surface px-3 py-2 shadow-card"
                />
              </Field>
              {!forUser && (
                <Alert variant="info">
                  {approvalRequired ? t("manual.add.needsApproval") : t("manual.add.noApproval")}
                </Alert>
              )}
            </>
          )}
          {otherError && <Alert variant="danger">{otherError}</Alert>}
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t("common.cancel")}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!valid || create.isPending}>
              {forUser || !approvalRequired ? t("manual.add.submitDirect") : t("manual.add.submit")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
